/**
 * `?demo=1` fixtures for the history box's whole record (TED #2503): an Imperial Brands-like listing
 * (`/s/LSE/IMB/dividend?demo=1`) paying an interim and a final from 1999 and quarterly from 2015,
 * with rights issues in 2002 and 2008 that restate the earlier amounts, and the history list rows in
 * the shape `stock/<tradingitem_id>/history/list` answers. Example data, never live figures.
 */
import { ListQueryParams } from "@/models/table";
import { DivAnnual, DivEventRow, DivHistory, DivHistoryGridRow, DivLadderRow, DivPayment, DivStockResponse } from "@/models/dividends-v2";
import { columns } from "../fixtures/grid";
import { demoPublication } from "../fixtures";

/** `[ex-date, pay date, announced, amount as declared (GBP), "I" interim | "F" final | "Q" quarterly]`. */
type Paid = [string, string, string, number, "I" | "F" | "Q"];

const PAID: Paid[] = [
    ["1999-05-24", "1999-08-13", "1999-05-19", 0.088, "I"],
    ["1999-12-06", "2000-02-11", "1999-12-01", 0.187, "F"],
    ["2000-07-10", "2000-08-11", "2000-07-05", 0.101, "I"],
    ["2001-01-22", "2001-02-23", "2001-01-17", 0.216, "F"],
    ["2001-07-11", "2001-08-10", "2001-07-06", 0.108, "I"],
    ["2002-01-23", "2002-02-22", "2002-01-18", 0.237, "F"],
    ["2002-07-10", "2002-08-09", "2002-07-05", 0.12, "I"],
    ["2003-01-22", "2003-02-21", "2003-01-17", 0.23, "F"],
    ["2003-07-09", "2003-08-08", "2003-07-04", 0.12, "I"],
    ["2004-01-21", "2004-02-20", "2004-01-16", 0.3, "F"],
    ["2004-07-07", "2004-08-06", "2004-07-02", 0.15, "I"],
    ["2005-01-19", "2005-02-18", "2005-01-14", 0.35, "F"],
    ["2005-07-06", "2005-08-05", "2005-07-01", 0.165, "I"],
    ["2006-01-18", "2006-02-17", "2006-01-13", 0.395, "F"],
    ["2006-07-05", "2006-08-04", "2006-06-30", 0.185, "I"],
    ["2007-01-17", "2007-02-16", "2007-01-12", 0.435, "F"],
    ["2007-07-11", "2007-08-10", "2007-07-06", 0.21, "I"],
    ["2008-01-16", "2008-02-15", "2008-01-11", 0.485, "F"],
    ["2008-06-04", "2008-08-08", "2008-05-30", 0.24, "I"],
    ["2009-01-21", "2009-02-20", "2009-01-16", 0.422, "F"],
    ["2009-06-17", "2009-08-19", "2009-06-12", 0.21, "I"],
    ["2010-01-20", "2010-02-19", "2010-01-15", 0.52, "F"],
    ["2010-07-21", "2010-08-20", "2010-07-16", 0.243, "I"],
    ["2011-01-19", "2011-02-18", "2010-11-02", 0.6, "F"],
    ["2011-07-20", "2011-08-19", "2011-05-10", 0.281, "I"],
    ["2012-01-18", "2012-02-17", "2011-11-01", 0.67, "F"],
    ["2012-07-18", "2012-08-17", "2012-05-01", 0.317, "I"],
    ["2013-01-16", "2013-02-18", "2012-10-30", 0.739, "F"],
    ["2013-07-17", "2013-08-16", "2013-04-30", 0.352, "I"],
    ["2014-01-15", "2014-02-17", "2013-11-05", 0.812, "F"],
    ["2014-07-16", "2014-08-19", "2014-05-07", 0.388, "I"],
    ["2015-01-15", "2015-02-17", "2014-11-04", 0.893, "F"],
    ["2015-05-28", "2015-06-30", "2015-05-06", 0.214, "Q"],
    ["2015-08-27", "2015-09-30", "2015-05-06", 0.214, "Q"],
    ["2015-11-19", "2015-12-31", "2015-11-03", 0.491, "Q"],
    ["2016-02-04", "2016-03-31", "2016-01-05", 0.491, "Q"],
    ["2016-05-19", "2016-06-30", "2016-05-04", 0.235, "Q"],
    ["2016-08-18", "2016-09-30", "2016-05-04", 0.235, "Q"],
    ["2016-11-17", "2016-12-30", "2016-11-08", 0.541, "Q"],
    ["2017-02-16", "2017-03-31", "2016-11-08", 0.541, "Q"],
    ["2017-05-18", "2017-06-30", "2017-05-03", 0.2585, "Q"],
    ["2017-08-17", "2017-09-29", "2017-05-03", 0.2585, "Q"],
    ["2017-11-16", "2017-12-29", "2017-11-07", 0.5951, "Q"],
    ["2018-02-22", "2018-03-29", "2018-01-10", 0.5951, "Q"],
    ["2018-05-24", "2018-06-29", "2018-05-09", 0.28435, "Q"],
    ["2018-08-23", "2018-09-28", "2018-05-09", 0.28435, "Q"],
    ["2018-11-22", "2018-12-31", "2018-11-06", 0.6546, "Q"],
    ["2019-02-21", "2019-03-29", "2018-11-06", 0.6546, "Q"],
    ["2019-05-23", "2019-06-28", "2019-05-08", 0.3128, "Q"],
    ["2019-08-22", "2019-09-30", "2019-05-08", 0.3128, "Q"],
    ["2019-11-21", "2019-12-31", "2019-11-05", 0.72, "Q"],
    ["2020-02-20", "2020-03-31", "2019-11-05", 0.7201, "Q"],
    ["2020-05-28", "2020-06-30", "2020-05-19", 0.2085, "Q"],
    ["2020-08-20", "2020-09-30", "2020-05-19", 0.2085, "Q"],
    ["2020-11-26", "2020-12-31", "2020-11-17", 0.48, "Q"],
    ["2021-02-18", "2021-03-31", "2020-11-17", 0.4801, "Q"],
    ["2021-05-27", "2021-06-30", "2021-05-18", 0.2106, "Q"],
    ["2021-08-19", "2021-09-30", "2021-05-18", 0.2106, "Q"],
    ["2021-11-25", "2021-12-31", "2021-11-16", 0.4848, "Q"],
    ["2022-02-17", "2022-03-31", "2021-11-16", 0.4848, "Q"],
    ["2022-05-26", "2022-06-30", "2022-05-17", 0.2127, "Q"],
    ["2022-08-18", "2022-09-30", "2022-05-17", 0.2127, "Q"],
    ["2022-11-24", "2022-12-30", "2022-11-15", 0.4931, "Q"],
    ["2023-02-16", "2023-03-31", "2022-11-15", 0.4932, "Q"],
    ["2023-05-25", "2023-06-30", "2023-05-16", 0.2159, "Q"],
    ["2023-08-17", "2023-09-29", "2023-05-16", 0.2159, "Q"],
    ["2023-11-23", "2023-12-29", "2023-11-20", 0.5182, "Q"],
    ["2024-02-15", "2024-03-28", "2023-11-20", 0.5182, "Q"],
    ["2024-05-23", "2024-06-28", "2024-05-15", 0.2245, "Q"],
    ["2024-08-22", "2024-09-30", "2024-05-15", 0.2245, "Q"],
    ["2024-11-28", "2024-12-31", "2024-10-08", 0.5426, "Q"],
    ["2025-02-20", "2025-03-31", "2024-12-17", 0.5426, "Q"],
    ["2025-05-22", "2025-06-30", "2025-04-01", 0.4008, "Q"],
    ["2025-08-21", "2025-09-30", "2025-05-14", 0.4008, "Q"],
    ["2025-11-27", "2025-12-31", "2025-11-18", 0.4008, "Q"],
    ["2026-02-19", "2026-03-31", "2025-11-18", 0.4008, "Q"],
    ["2026-05-21", "2026-06-30", "2026-05-12", 0.4168, "Q"],
    ["2026-08-20", "2026-09-30", "2026-05-12", 0.4168, "Q"],
];

/** Rights issues restate the amounts before them into today's share count. */
const RIGHTS: [string, number][] = [
    ["2002-04-09", 1.19895],
    ["2008-05-21", 1.1535],
];

const factorOn = (ex: string): number => RIGHTS.reduce((f, [on, rate]) => (ex < on ? f * rate : f), 1);

const FREQUENCY_NAME = { I: "Interim Payment", F: "Final Payment", Q: "Quarterly" } as const;

const addDays = (iso: string, days: number): string => {
    const t = Date.parse(`${iso}T00:00:00Z`) + days * 86_400_000;
    return new Date(t).toISOString().slice(0, 10);
};

const ms = (iso: string | null): number | null => (iso ? Date.parse(`${iso}T00:00:00Z`) : null);

const round = (v: number, places = 6) => Math.round(v * 10 ** places) / 10 ** places;

/** Imperial's demo tradingitem id (a stand-in, as every demo id is). */
export const IMB_DEMO_ID = 20172869;

const imbRows = (): DivHistoryGridRow[] =>
    PAID.map(([ex, pay, announced, declared, cadence], i) => {
        const factor = factorOn(ex);
        return {
            id: `${ex}-${i}`,
            exDate: ms(ex),
            payDate: ms(pay),
            recordDate: ms(addDays(ex, 1)),
            announcedDate: ms(announced),
            amount: round(declared / factor, 8),
            amountDeclared: declared,
            factor: round(factor, 8),
            currency: "GBP",
            frequency: FREQUENCY_NAME[cadence],
            kind: "regular" as const,
            dividendType: "No special code applies",
            locked: false,
        };
    }).reverse();

const periodLabel = (ex: string, cadence: "I" | "F" | "Q"): string => {
    const [y, m] = ex.split("-").map(Number);
    if (cadence === "I") return `Interim ${y}`;
    if (cadence === "F") return `Final ${m <= 2 ? y - 1 : y}`;
    return `Q${Math.floor((m - 1) / 3) + 1} ${y}`;
};

/** The Imperial demo's forecast years after this one: years ahead, central total, low, high, share still paying. */
const IMB_HORIZON_YEARS: [number, number, number, number, number][] = [
    [1, 1.7088, 1.62, 1.79, 0.98],
    [2, 1.75, 1.58, 1.92, 0.96],
    [3, 1.79, 1.54, 2.04, 0.93],
    [4, 1.83, 1.5, 2.16, 0.9],
];

/** The Imperial demo's payments to come, out to the end of the horizon: label, ex-date, central, low, high. */
const IMB_HORIZON_PAYMENTS: [string, string, number, number, number][] = [
    ["Q4 2026", "2026-11-26", 0.4168, 0.41, 0.42],
    ["Q1 2027", "2027-02-18", 0.4168, 0.41, 0.43],
    ["Q2 2027", "2027-05-20", 0.4272, 0.41, 0.45],
    ["Q3 2027", "2027-08-19", 0.4272, 0.4, 0.45],
    ["Q4 2027", "2027-11-25", 0.4376, 0.4, 0.46],
    ["Q1 2028", "2028-02-17", 0.4376, 0.4, 0.47],
    ["Q2 2028", "2028-05-18", 0.4376, 0.39, 0.48],
    ["Q3 2028", "2028-08-17", 0.4376, 0.39, 0.48],
    ["Q4 2028", "2028-11-23", 0.4372, 0.39, 0.49],
    ["Q1 2029", "2029-02-15", 0.4475, 0.38, 0.5],
    ["Q2 2029", "2029-05-17", 0.4475, 0.38, 0.51],
    ["Q3 2029", "2029-08-16", 0.4475, 0.38, 0.51],
    ["Q4 2029", "2029-11-22", 0.4475, 0.37, 0.52],
    ["Q1 2030", "2030-02-14", 0.4575, 0.37, 0.53],
    ["Q2 2030", "2030-05-16", 0.4575, 0.37, 0.54],
    ["Q3 2030", "2030-08-15", 0.4575, 0.36, 0.54],
    ["Q4 2030", "2030-11-21", 0.4575, 0.36, 0.55],
];

/** The history block of the Imperial demo: annual totals and every payment from 1999, today's shares, then the horizon. */
const imbHistory = (): DivHistory => {
    const byYear = new Map<number, number>();
    const quarterly: DivPayment[] = PAID.map(([ex, , , declared, cadence]) => {
        const amount = round(declared / factorOn(ex));
        const year = Number(ex.slice(0, 4));
        byYear.set(year, round((byYear.get(year) ?? 0) + amount));
        return { period_label: periodLabel(ex, cadence), ex_date: ex, amount, forecast: false };
    });
    const annual: DivAnnual[] = Array.from(byYear.entries()).map(([year, amount]) => ({ year, amount, forecast: false, amount_p10: null, amount_p90: null }));
    // This year's total with the November payment still to come, then the four-year horizon (TED #2458):
    // the central total and a range that widens with the years, and the share of payers still paying.
    const last = annual[annual.length - 1];
    const sofar = last.amount ?? 0;
    annual[annual.length - 1] = { ...last, amount: round(sofar + 0.4168), forecast: true, amount_p10: round(sofar + 0.405), amount_p90: round(sofar + 0.428), paying_share: 0.99 };
    for (const [k, amount, p10, p90, share] of IMB_HORIZON_YEARS) annual.push({ year: last.year + k, amount, forecast: true, amount_p10: p10, amount_p90: p90, paying_share: share });
    return {
        annual,
        quarterly: quarterly.concat(IMB_HORIZON_PAYMENTS.map(([label, ex, amount, p10, p90]) => ({ period_label: label, ex_date: ex, amount, forecast: true, amount_p10: p10, amount_p90: p90 }))),
        growth_1y_pct: 15.6,
        growth_5y_cagr_pct: 1.5,
        raises_in_a_row: 1,
        cuts_since_2012: 2,
        specials: 0,
    };
};

/** Apple's amounts in the ladder, scaled to Imperial's level, in pounds. */
const AMOUNT_FIELDS = ["last_amount", "amount_p10", "amount_p50", "amount_p90", "declared_amount", "options_implied"] as const;
const SCALE = 1.6;

const scaleRow = <T extends DivEventRow>(r: T): T => {
    const out: T = { ...r, ticker: "IMB", company_name: "Imperial Brands", exchange_symbol: "LSE", tradingitem_id: IMB_DEMO_ID, country: "GB", currency: "GBP", sector: "Consumer Staples" };
    for (const f of AMOUNT_FIELDS) {
        const v = r[f];
        if (typeof v === "number") (out as Record<string, unknown>)[f] = round(v * SCALE, 4);
    }
    return out;
};

/**
 * The Imperial Brands demo payload: the Apple fixture's tiles and ladder re-dressed in pounds at
 * Imperial's level, with its own dividend history back to 1999 and none of Apple's own words.
 */
export const imperialPayload = (apple: DivStockResponse): DivStockResponse => ({
    ...apple,
    tradingitem_id: IMB_DEMO_ID,
    ticker: "IMB",
    exchange_symbol: "LSE",
    company_name: "Imperial Brands",
    currency: "GBP",
    next: apple.next ? scaleRow(apple.next) : null,
    ladder: apple.ladder ? apple.ladder.map((r) => scaleRow(r as DivLadderRow)) : null,
    yield: apple.yield
        ? {
              ...apple.yield,
              forward_12m_pct: 6.4,
              forward_12m_amount: 1.71,
              trailing_12m_pct: 6.3,
              trailing_12m_amount: 1.65,
              sector_median_pct: 3.1,
              sector: "Consumer Staples",
              streak_years: 1,
              payments_per_year: 4,
          }
        : null,
    pattern: "Declared with results, ex-date in the third week of February, May, August and November",
    commentary: null,
    history: imbHistory(),
    policy: apple.policy ? { ...apple.policy, type: "variable", quote: null, source_label: null, source_url: null, last_change_pct: 4.0, last_change_date: "2026-05-12" } : null,
    options: apple.options ? apple.options.map((o) => ({ ...o, implied: o.implied === null ? null : round(o.implied * SCALE, 4), forecast: o.forecast === null ? null : round(o.forecast * SCALE, 4) })) : null,
    lists: null,
    peers: null,
});

/** Rows for a demo payload without its own record: its past payments, the rest of the row unknown. */
const rowsFromHistory = (p: DivStockResponse): DivHistoryGridRow[] =>
    (p.history?.quarterly ?? [])
        .filter((q) => !q.forecast && q.ex_date && typeof q.amount === "number")
        .map((q, i) => ({
            id: `${q.ex_date}-${i}`,
            exDate: ms(q.ex_date),
            payDate: ms(addDays(q.ex_date as string, 3)),
            recordDate: ms(q.ex_date),
            announcedDate: ms(addDays(q.ex_date as string, -11)),
            amount: q.amount,
            amountDeclared: q.amount,
            factor: 1,
            currency: p.currency,
            frequency: "Quarterly",
            kind: "regular" as const,
            dividendType: "No special code applies",
            locked: !!p.locked,
        }))
        .reverse();

/** The history list's columns as `stock/<tradingitem_id>/history/list` sends them. */
const HISTORY_COLUMNS = columns([
    { field: "exDate", headerName: "Ex-date", format: "date", pinDirection: "left" },
    { field: "amount", headerName: "Amount", description: "Per share, restated into today's share count for splits and rights issues" },
    { field: "amountDeclared", headerName: "As declared", description: "Per share, as the company declared it" },
    { field: "payDate", headerName: "Pay date", format: "date" },
    { field: "recordDate", headerName: "Record date", format: "date" },
    { field: "announcedDate", headerName: "Announced", format: "date", description: "When the dividend was announced" },
    { field: "frequency", headerName: "Frequency", filterType: "select", filterOptions: ["Final Payment", "Interim Payment", "Quarterly"], description: "How often the dividend is paid, as recorded for this payment" },
    { field: "kind", headerName: "Kind", filterType: "select", filterOptions: ["regular"] },
    { field: "currency", headerName: "Currency", filterType: "select", filterOptions: ["GBP"] },
    { field: "dividendType", headerName: "Dividend type", filterType: "select", filterOptions: ["No special code applies"], description: "The type recorded for this dividend" },
    { field: "factor", headerName: "Adjustment", hide: true },
    { field: "locked", hide: true },
]);

type SortKey = keyof DivHistoryGridRow;

const sortRows = (rows: DivHistoryGridRow[], sortModel: unknown): { rows: DivHistoryGridRow[]; model: Record<string, string> } => {
    let model: Record<string, string> = { exDate: "desc" };
    let parsed: unknown = sortModel;
    if (typeof sortModel === "string") {
        try {
            parsed = JSON.parse(sortModel);
        } catch {
            parsed = null;
        }
    }
    if (parsed && typeof parsed === "object" && Object.keys(parsed).length) model = parsed as Record<string, string>;
    const [field, dir] = Object.entries(model)[0] as [SortKey, string];
    const sign = dir === "asc" ? 1 : -1;
    const sorted = [...rows].sort((a, b) => {
        const x = a[field];
        const y = b[field];
        if (x === y) return 0;
        if (x === null || x === undefined) return 1;
        if (y === null || y === undefined) return -1;
        return (x < y ? -1 : 1) * sign;
    });
    return { rows: sorted, model };
};

/** The demo history list for a demo payload: Imperial's whole record, else the payload's own payments. */
export const demoHistoryGridFetcher =
    (payload: DivStockResponse) =>
    async (params: ListQueryParams): Promise<any> => {
        const all = payload.ticker === "IMB" ? imbRows() : rowsFromHistory(payload);
        const query = (params.filter_options ?? {}) as Record<string, unknown>;
        const { rows, model } = sortRows(all, query.sortModel);
        const page = rows.slice((params.page - 1) * params.page_size, params.page * params.page_size);
        const pub = demoPublication();
        return {
            ...HISTORY_COLUMNS,
            rows: page,
            length: rows.length,
            paginationLinks: null,
            sortModel: model,
            locked: !!payload.locked,
            upgrade: payload.locked ? "advanced" : null,
            published: pub.published,
            publishedCountries: pub.published_countries,
        };
    };
